/**
 * « Messages » dans l'en-tête — refonte UX, chantier 2 (C1, maquette 1, validée le 24/09).
 *
 * Constat : la cloche n'existait QUE sur l'accueil (elle disparaissait dès qu'on
 * entrait dans une brique) et la messagerie interne n'était atteignable QUE par le
 * menu du GRC. Ce bouton, dans l'en-tête de tous les écrans, réunit :
 *   · Notifications (celles de la cloche, mêmes appels) ;
 *   · Messagerie interne (messages reçus non lus) ;
 *   · Diffusions à accuser (« lu », « pris en compte »), avec le bouton d'accusé.
 * Un seul compteur. Les habitants ne voient que leurs notifications.
 * Aucun droit ne change : ce sont les mêmes routes, déjà gardées côté serveur.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { api, type NotificationItem, type InboxMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type { DiffusionAAcquitter } from '@roronoa-collectivites/shared-types';

type Onglet = 'notifications' | 'messagerie' | 'diffusions';
const quand = (iso: string) => {
  const d = new Date(iso); const auj = new Date();
  return d.toDateString() === auj.toDateString()
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

export function MessagesMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const interne = !!user && user.role !== 'administre';
  const [ouvert, setOuvert] = useState(false);
  const [onglet, setOnglet] = useState<Onglet>('notifications');
  const [nb, setNb] = useState({ notifications: 0, messagerie: 0, diffusions: 0 });
  const [notifs, setNotifs] = useState<NotificationItem[]>([]);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [diffusions, setDiffusions] = useState<DiffusionAAcquitter[]>([]);
  const zone = useRef<HTMLDivElement>(null);

  const compter = useCallback(async () => {
    const [n, m, d] = await Promise.all([
      api.notifications.unreadCount().then((r) => r.count).catch(() => 0),
      interne ? api.messagerie.unreadCount().then((r) => r.count).catch(() => 0) : Promise.resolve(0),
      interne ? api.diffusions.mesAttentes().then((r) => r.length).catch(() => 0) : Promise.resolve(0),
    ]);
    setNb({ notifications: n, messagerie: m, diffusions: d });
  }, [interne]);

  useEffect(() => {
    if (!user) return;
    void compter();
    const t = window.setInterval(() => { if (!ouvert) void compter(); }, 60_000);
    return () => window.clearInterval(t);
  }, [user, ouvert, compter]);

  // Fermer au clic extérieur et à Échap (clavier, RGAA).
  useEffect(() => {
    if (!ouvert) return;
    const clic = (e: MouseEvent) => { if (zone.current && !zone.current.contains(e.target as Node)) setOuvert(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', clic); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', clic); document.removeEventListener('keydown', esc); };
  }, [ouvert]);

  async function ouvrir() {
    const o = !ouvert; setOuvert(o);
    if (!o) return;
    const [n, m, d] = await Promise.all([
      api.notifications.list().then((r) => r.items).catch(() => [] as NotificationItem[]),
      interne ? api.messagerie.inbox({ limit: 8 }).then((r) => r.items).catch(() => []) : Promise.resolve([]),
      interne ? api.diffusions.mesAttentes().catch(() => []) : Promise.resolve([]),
    ]);
    setNotifs((n as NotificationItem[]).slice(0, 12)); setMessages(m as InboxMessage[]); setDiffusions(d as DiffusionAAcquitter[]);
  }

  async function toutLu() {
    await api.notifications.markAllRead().catch(() => undefined);
    setNotifs((l) => l.map((x) => ({ ...x, lu: true }))); void compter();
  }
  async function accuser(id: string) {
    await api.diffusions.acquitter(id).catch(() => undefined);
    setDiffusions((l) => l.filter((x) => x.id !== id)); void compter();
  }
  function aller(lien: string | null) { setOuvert(false); if (lien) navigate(lien); }

  if (!user) return null;
  const total = nb.notifications + nb.messagerie + nb.diffusions;
  const onglets: Array<[Onglet, string, number]> = interne
    ? [['notifications', 'Notifications', nb.notifications], ['messagerie', 'Messagerie', nb.messagerie], ['diffusions', 'Diffusions', nb.diffusions]]
    : [['notifications', 'Notifications', nb.notifications]];

  return (
    <div className="relative" ref={zone}>
      <button type="button" onClick={() => void ouvrir()} aria-expanded={ouvert} aria-haspopup="dialog"
        aria-label={`Messages${total ? `, ${total} non lu${total > 1 ? 's' : ''}` : ''}`}
        className={`inline-flex h-10 items-center gap-2 rounded-r-md border px-3 text-sm font-medium text-ink ${ouvert ? 'border-tech/60 bg-tech/15' : 'border-border bg-surface/60 hover:bg-surface-hi/60'}`}>
        <MessageCircle className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Messages</span>
        {total > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-r-pill bg-urgent px-1.5 text-2xs font-semibold text-white">{total > 99 ? '99+' : total}</span>}
      </button>

      {ouvert && (
        <section role="dialog" aria-label="Messages"
          className="absolute right-0 top-12 z-50 w-[min(26rem,calc(100vw-1.5rem))] overflow-hidden rounded-r-xl border border-border bg-surface shadow-soft">
          <div className="flex items-center border-b border-border px-4 py-3">
            <span className="flex-1 text-sm font-semibold text-ink">Messages</span>
            {onglet === 'notifications' && notifs.some((x) => !x.lu) && (
              <button type="button" className="text-xs text-accent hover:underline" onClick={() => void toutLu()}>Tout marquer comme lu</button>
            )}
          </div>
          <div role="tablist" aria-label="Type de messages" className="flex gap-1 border-b border-border px-3 py-2">
            {onglets.map(([k, l, n]) => (
              <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
                className={`h-8 rounded-r-pill border px-3 text-xs font-medium ${onglet === k ? 'border-tech/50 bg-tech/15 text-tech-fg' : 'border-transparent text-ink-muted hover:text-ink'}`}>
                {l}{n > 0 ? ` ${n}` : ''}
              </button>
            ))}
          </div>

          <div className="max-h-[26rem] overflow-y-auto" role="tabpanel">
            {onglet === 'notifications' && (notifs.length === 0
              ? <p className="px-4 py-6 text-sm text-ink-subtle">Aucune notification.</p>
              : notifs.map((x) => (
                <button key={x.id} type="button" onClick={() => aller(x.lien)}
                  className="flex w-full gap-3 border-b border-border/60 px-4 py-3 text-left hover:bg-surface-hi/40">
                  <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-ink">{x.titre}</span>
                    {x.corps && <span className="mt-0.5 block truncate text-xs text-ink-muted">{x.corps}</span>}</span>
                  <span className="flex flex-col items-end gap-1.5"><span className="text-2xs text-ink-subtle">{quand(x.createdAt)}</span>
                    {!x.lu && <span className="h-2 w-2 rounded-full bg-tech" aria-label="non lu" />}</span>
                </button>)))}

            {onglet === 'messagerie' && (messages.length === 0
              ? <p className="px-4 py-6 text-sm text-ink-subtle">Aucun message.</p>
              : messages.map((m) => (
                <button key={m.id} type="button" onClick={() => aller(`/grc/messagerie?message=${m.id}`)}
                  className="flex w-full gap-3 border-b border-border/60 px-4 py-3 text-left hover:bg-surface-hi/40">
                  <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-ink">{m.objet}</span>
                    <span className="block text-xs text-ink-muted">{m.expediteurNom}</span></span>
                  <span className="flex flex-col items-end gap-1.5"><span className="text-2xs text-ink-subtle">{quand(m.createdAt)}</span>
                    {!m.lu && <span className="h-2 w-2 rounded-full bg-tech" aria-label="non lu" />}</span>
                </button>)))}

            {onglet === 'diffusions' && (diffusions.length === 0
              ? <p className="px-4 py-6 text-sm text-ink-subtle">Aucune diffusion en attente.</p>
              : diffusions.map((d) => (
                <div key={d.id} className="border-b border-border/60 px-4 py-3">
                  <p className="text-sm font-medium text-ink">{d.objet}</p>
                  <p className="text-xs text-ink-muted">{d.expediteur} · {quand(d.envoyeAt)}{d.dateLimite ? ` · avant le ${new Date(d.dateLimite).toLocaleDateString('fr-FR')}` : ''}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-ink">{d.corps}</p>
                  <button type="button" onClick={() => void accuser(d.id)}
                    className="mt-2 h-8 rounded-r-md border border-border px-3 text-xs font-medium text-ink hover:bg-surface-hi/60">
                    {d.retourAttendu === 'prise_connaissance' ? 'J’en ai pris connaissance' : 'C’est fait'}
                  </button>
                </div>)))}
          </div>

          {interne && (
            <div className="flex justify-between px-4 py-3 text-xs">
              <button type="button" className="text-accent hover:underline" onClick={() => aller('/grc/messagerie')}>Ouvrir la messagerie</button>
              <button type="button" className="text-accent hover:underline" onClick={() => aller('/diffusions/nouvelle')}>Écrire une diffusion</button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
